import {
  centsToDollars,
  perStudentPriceInCents,
} from "@outschool/business-rules";
import * as FilestackUrls from "@outschool/filestack-urls";
import {
  ActivityMetaTags_ActivityFragment,
  ActivityMetaTags_SectionFragment,
} from "@outschool/gql-frontend-generated";
import { BASE_CURRENCY, useMachineTranslation } from "@outschool/localization";
import { createUrlWithActivity } from "@outschool/routes";
import { gql } from "@outschool/ui-apollo";
import _ from "lodash";
import React from "react";

import * as Activity from "../../../shared/Activity";
import { useClassPageTitle } from "../../../shared/PageMetadata";
import { useCourseMetadata } from "../../hooks/Activity";
import HeaderTags from "../HeaderTags";

type ActivityMetaTagsProps = {
  activity: ActivityMetaTags_ActivityFragment;
  section?: ActivityMetaTags_SectionFragment;
};

export default function ActivityMetaTags({
  activity,
  section,
}: ActivityMetaTagsProps) {
  const title = useClassPageTitle(activity);

  // both dimensions must be > 200 for Facebook to accept as
  // link preview
  const previewImageWidth = 2 * Activity.MAIN_IMAGE_WIDTH;
  const previewImageHeight = 2 * Activity.MAIN_IMAGE_HEIGHT;

  const imageUrl = FilestackUrls.imageUrl(activity.details.photo, {
    w: previewImageWidth,
    h: previewImageHeight,
    fit: "crop",
  });

  const priceInDollars = centsToDollars(
    perStudentPriceInCents(activity, section),
  );

  // @ts-ignore TS(2322): Type 'ActivityMetaTags_SectionFragment | undefined... Remove this comment to see the full error message
  const courseMetadata = useCourseMetadata({ activity, section });
  const scriptMetadata = _.compact([courseMetadata]);

  const { translate } = useMachineTranslation();
  // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
  const descriptionT = translate(activity?.summary);

  const metatags = [
    { property: "og:title", content: title },
    // Slice to address SEO length warnings
    { name: "description", content: descriptionT?.slice(0, 155) },
    { property: "og:description", content: descriptionT },
    {
      property: "og:url",
      content: createUrlWithActivity(activity),
    },
    { property: "og:image", content: imageUrl },
    { property: "og:image:height", content: previewImageHeight },
    { property: "og:image:width", content: previewImageWidth },
    { name: "twitter:card", content: "summary_large_image" },
    // Pinterest Tags - https://developers.pinterest.com/docs/rich-pins/products/
    { property: "og:type", content: "product" },
    { property: "product:price:amount", content: `${priceInDollars}` },
    // @ts-ignore TS(2339): Property 'code' does not exist on type 'CurrencyCo... Remove this comment to see the full error message
    { property: "product:price:currency", content: BASE_CURRENCY.code.code },
  ];

  return (
    <HeaderTags
      // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      title={title}
      meta={metatags}
      link={[
        {
          rel: "canonical",
          href: createUrlWithActivity(activity),
        },
      ]}
      script={scriptMetadata}
    />
  );
}

ActivityMetaTags.fragments = {
  activity: gql`
    fragment ActivityMetaTags_activity on Activity {
      uid
      slug_id
      title
      titleTranslated
      summary
      age_min
      age_max
      published_at
      price_cents
      averageStarRating
      reviewCount
      duration_minutes
      is_ongoing_weekly
      leader {
        uid
        name
        leader_link
      }
      details {
        photo
      }
      isClub
    }
  `,
  section: gql`
    fragment ActivityMetaTags_section on Section {
      uid
      price_cents
    }
  `,
};
